"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, Check, ListTree, Sparkles } from "lucide-react";

export interface TocItem {
  id: string;
  text: string;
}

/** Which section is on screen right now (the last heading scrolled past the top third). */
function useActive(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  useEffect(() => {
    if (!ids.length) return;
    const els = ids.map((id) => document.getElementById(id)).filter((e): e is HTMLElement => !!e);
    if (!els.length) return;
    let raf = 0;
    const pick = () => {
      raf = 0;
      const line = window.innerHeight * 0.3;
      let current = els[0].id;
      for (const el of els) {
        if (el.getBoundingClientRect().top - line <= 0) current = el.id;
        else break;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(pick);
    };
    pick();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids]);
  return active;
}

function TocList({ items, active, onPick }: { items: TocItem[]; active: string | null; onPick?: () => void }) {
  return (
    <ul className="space-y-0.5 border-l border-line text-[0.88rem]">
      {items.map((h) => {
        const on = h.id === active;
        return (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              onClick={onPick}
              aria-current={on ? "location" : undefined}
              className={`-ml-px block border-l-2 py-1.5 pl-4 leading-snug transition-colors ${
                on
                  ? "border-violet font-semibold text-violet-deep"
                  : "border-transparent text-ink-soft hover:border-violet-light hover:text-ink"
              }`}
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/** The colourful "start free" card: under the contents on desktop, after the article on phones. */
export function StartFreeCard({ className = "" }: { className?: string }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[26px] bg-[linear-gradient(150deg,#1c1548_0%,#3b23a8_48%,#8f67ff_100%)] p-6 text-white shadow-[0_28px_60px_-28px_rgba(110,76,232,0.9)] ${className}`}
    >
      <span aria-hidden className="pointer-events-none absolute -right-14 -top-16 h-44 w-44 rounded-full bg-[radial-gradient(closest-side,rgba(255,143,207,0.75),transparent)]" />
      <span aria-hidden className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-[radial-gradient(closest-side,rgba(197,140,240,0.55),transparent)]" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[0.72rem] font-bold uppercase tracking-[0.08em] ring-1 ring-white/25">
          <Sparkles size={12} /> Free to start
        </span>
        <p className="mt-3 text-[1.25rem] font-extrabold leading-tight tracking-[-0.01em]">
          Rank faster with Rankbist
        </p>
        <p className="mt-1.5 text-[0.88rem] leading-relaxed text-white/75">
          The automated SEO engine that finds what holds your pages back and fixes it.
        </p>
        <ul className="mt-4 space-y-2 text-[0.86rem]">
          {["Free plan, no card needed", "AI fixes in one click", "Real Google rankings and indexing"].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <span className="mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-[#34d399] text-[#0b3b2a]">
                <Check size={11} strokeWidth={3.2} />
              </span>
              {t}
            </li>
          ))}
        </ul>
        <Link
          href="/signup"
          className="group mt-5 flex h-11 items-center justify-center gap-2 rounded-full bg-white text-[0.92rem] font-extrabold text-violet-deep shadow-[0_12px_28px_-12px_rgba(0,0,0,0.6)] transition-transform hover:-translate-y-0.5"
        >
          Start for free <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
        <Link href="/pricing" className="mt-3 block text-center text-[0.8rem] font-semibold text-white/70 underline-offset-4 hover:text-white hover:underline">
          See packages and prices
        </Link>
      </div>
    </div>
  );
}

/** Desktop right column: "On this page" (follows your reading) with the start-free card under it. */
export function PostSidebar({ items }: { items: TocItem[] }) {
  const active = useActive(items.map((i) => i.id));
  return (
    <aside className="hidden lg:block" aria-label="On this page">
      <div className="sticky top-28 flex max-h-[calc(100dvh-8rem)] flex-col gap-6">
        {items.length > 0 && (
          <nav aria-label="On this page" className="flex min-h-0 flex-col rounded-[22px] border border-line bg-white p-5 shadow-[0_18px_40px_-30px_rgba(20,18,27,0.35)]">
            <p className="mb-3 flex items-center gap-2 text-[0.8rem] font-extrabold uppercase tracking-[0.08em] text-ink-soft">
              <ListTree size={15} className="text-violet" /> On this page
            </p>
            <div className="rb-scroll -mr-2 min-h-0 overflow-y-auto pr-2">
              <TocList items={items} active={active} />
            </div>
          </nav>
        )}
        <StartFreeCard className="flex-shrink-0" />
      </div>
    </aside>
  );
}

/** Phones and tablets: the contents fold under the cover image. */
export function MobileToc({ items }: { items: TocItem[] }) {
  const active = useActive(items.map((i) => i.id));
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="mt-6 rounded-[20px] border border-line bg-white lg:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-[0.92rem] font-extrabold"
      >
        <span className="flex items-center gap-2">
          <ListTree size={16} className="text-violet" /> On this page
        </span>
        <span aria-hidden className={`flex h-7 w-7 items-center justify-center rounded-full bg-violet-pale text-violet-deep transition-transform ${open ? "rotate-45" : ""}`}>
          +
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4">
          <TocList items={items} active={active} onPick={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
